import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { DOCLINK_COLORS } from "@/config/colors";
import {
  getDocLinkConsultTypeColor,
  getDocLinkConsultTypeLabel,
} from "@/config/docLinkConsultTypes";
import { getDocLinkConsultStatusLabel } from "@/config/docLinkConsultStatus";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import { formatHubMedTimeSydney } from "@/utils/hubMedFormatting";

const MAX_ROWS = 50;

const ymdLocal = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const normalizeLogRow = (r) => {
  if (!r) return null;
  const rawPhType = r.phConsultType ?? r.PHConsultType;
  const phTypeNum =
    rawPhType === undefined || rawPhType === null || rawPhType === ""
      ? null
      : Number(rawPhType);
  const phConsultType = Number.isFinite(phTypeNum) ? phTypeNum : null;

  const rawConsultStatus = r.consultStatus ?? r.ConsultStatus;
  const consultStatusNum =
    rawConsultStatus === undefined || rawConsultStatus === null || rawConsultStatus === ""
      ? null
      : Number(rawConsultStatus);
  const consultStatus = Number.isFinite(consultStatusNum) ? consultStatusNum : null;

  return {
    consultId: r.consultId ?? r.ConsultId,
    phUuid: r.phUuid ?? r.PhUuid ?? r.PHUuid,
    firstName: r.firstName ?? r.FirstName,
    lastName: r.lastName ?? r.LastName,
    createDateTime: r.createDateTime ?? r.CreateDateTime,
    staffName: r.staffName ?? r.StaffName,
    /** Legacy string from API when `consultStatus` is not sent. */
    statusLabel: r.statusLabel ?? r.StatusLabel,
    consultStatus,
    chemistName: r.chemistName ?? r.ChemistName,
    phConsultType,
  };
};

const dailyLogStatusText = (row) => {
  if (row.consultStatus !== null && row.consultStatus !== undefined) {
    return getDocLinkConsultStatusLabel(row.consultStatus);
  }
  const legacy = row.statusLabel;
  if (legacy != null && legacy !== "") return String(legacy);
  return "—";
};

/** WCAG-ish contrast for text on solid hex fill. */
const contrastOnHex = (hex) => {
  if (!hex || typeof hex !== "string" || !/^#[0-9a-fA-F]{6}$/.test(hex)) {
    return "#1e1b4b";
  }
  const h = hex.slice(1);
  const R = parseInt(h.slice(0, 2), 16) / 255;
  const G = parseInt(h.slice(2, 4), 16) / 255;
  const B = parseInt(h.slice(4, 6), 16) / 255;
  const L = 0.2126 * R + 0.7152 * G + 0.0722 * B;
  return L > 0.55 ? "#1e1b4b" : "#ffffff";
};

const getConsultTypeVisual = (rawType) => {
  const n = Number(rawType);
  const hasEnum =
    rawType !== null &&
    rawType !== undefined &&
    rawType !== "" &&
    Number.isFinite(n);
  const bg = hasEnum ? getDocLinkConsultTypeColor(n) : "#64748b";
  let label = "—";
  if (hasEnum) label = getDocLinkConsultTypeLabel(n);
  else if (rawType !== null && rawType !== undefined && rawType !== "") {
    label = String(rawType);
  }
  return { bg, label };
};

/** Match overview chemists table (`docLinkPurpleDataGridSx`) */
const logHeaderCellSx = {
  backgroundColor: `${DOCLINK_COLORS.DARK_PURPLE} !important`,
  color: "#fff !important",
  fontWeight: 700,
  fontSize: 14,
  borderBottom: `2px solid ${DOCLINK_COLORS.DARK_PURPLE}`,
  py: 1.25,
  textAlign: "center",
};

const logBodyCellSx = {
  fontSize: 14,
  borderBottom: "1px solid #e5e7eb",
  py: 1,
  textAlign: "center",
  backgroundColor: "#fff",
};

const logBodyRowSx = {
  cursor: "default",
  "& > .MuiTableCell-root": {
    transition: "background-color 0.12s ease, filter 0.12s ease",
  },
  "&:hover > .MuiTableCell-root:not(.daily-log-type-cell)": {
    backgroundColor: `${DOCLINK_COLORS.LAVENDER_TINT} !important`,
  },
  "&:hover > .daily-log-type-cell": {
    filter: "brightness(0.96)",
  },
};

/**
 * HubMed admin daily appointment log (mounted only while open so the date defaults to today).
 */
const HubMedDailyLogDialog = ({ onClose }) => {
  const [dateStr, setDateStr] = useState(ymdLocal);
  const [rows, setRows] = useState([]);
  const [totalFromApi, setTotalFromApi] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!dateStr) return;
      setLoading(true);
      setError(null);
      try {
        const data = await phappsRequest(
          API_PATHS.HUBMED_ADMIN_DAILY_LOG,
          "GET",
          null,
          { date: dateStr }
        );
        const raw = data.dailyLog ?? data.DailyLog ?? [];
        const list = Array.isArray(raw)
          ? raw.map(normalizeLogRow).filter(Boolean)
          : [];
        list.sort((a, b) => {
          const ta = new Date(a.createDateTime || 0).getTime();
          const tb = new Date(b.createDateTime || 0).getTime();
          if (ta !== tb) return ta - tb;
          return (Number(a.consultId) || 0) - (Number(b.consultId) || 0);
        });
        if (!cancelled) {
          setTotalFromApi(list.length);
          setRows(list.slice(0, MAX_ROWS));
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setRows([]);
          setTotalFromApi(0);
          setError(getAdminApiErrorMessage(err, "Failed to load daily log"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dateStr]);

  const truncated = totalFromApi > MAX_ROWS;

  const titleDate = useMemo(() => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-").map(Number);
    if (!y || !m || !d) return dateStr;
    return new Intl.DateTimeFormat("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(new Date(y, m - 1, d));
  }, [dateStr]);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: `2px solid ${DOCLINK_COLORS.DARK_PURPLE}`,
          overflow: "hidden",
          boxShadow: "0 12px 40px rgba(64, 29, 94, 0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          color: DOCLINK_COLORS.DARK_PURPLE,
          fontWeight: 700,
          borderBottom: `1px solid ${DOCLINK_COLORS.LIGHT_PURPLE}`,
        }}
      >
        HubMed daily log{titleDate ? ` — ${titleDate}` : ""}
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          borderColor: `${DOCLINK_COLORS.LIGHT_PURPLE}99`,
          pt: 2,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {
              "Create times in the table use Sydney (Australia/Sydney). The day is the Log date selected above."
            }
          </Typography>

          <TextField
            label="Log date"
            type="date"
            size="small"
            value={dateStr}
            onChange={(e) => setDateStr(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ maxWidth: 220 }}
          />

          {loading ? <LinearProgress sx={{ borderRadius: 1 }} /> : null}
          {error ? <Alert severity="warning">{error}</Alert> : null}

          {!loading && !error && truncated ? (
            <Typography variant="caption" color="text.secondary">
              Showing first {MAX_ROWS} of {totalFromApi} appointments for this date.
            </Typography>
          ) : null}

          <Box
            sx={{
              border: `2px solid ${DOCLINK_COLORS.DARK_PURPLE}`,
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <TableContainer sx={{ maxHeight: 420, overflow: "auto" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={logHeaderCellSx}>Time (Sydney)</TableCell>
                    <TableCell sx={logHeaderCellSx}>Consult type</TableCell>
                    <TableCell sx={logHeaderCellSx}>Customer</TableCell>
                    <TableCell sx={logHeaderCellSx}>Chemist</TableCell>
                    <TableCell sx={logHeaderCellSx}>Staff</TableCell>
                    <TableCell sx={logHeaderCellSx}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && !loading ? (
                    <TableRow sx={logBodyRowSx}>
                      <TableCell colSpan={6} sx={{ ...logBodyCellSx, py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          No appointments for this date.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row, idx) => {
                      const name = [row.firstName, row.lastName].filter(Boolean).join(" ").trim();
                      const typeVis = getConsultTypeVisual(row.phConsultType);
                      return (
                        <TableRow
                          key={
                            row.consultId != null
                              ? `c-${row.consultId}`
                              : `r-${idx}-${row.phUuid || ""}`
                          }
                          sx={logBodyRowSx}
                        >
                          <TableCell sx={logBodyCellSx}>
                            {formatHubMedTimeSydney(row.createDateTime)}
                          </TableCell>
                          <TableCell
                            className="daily-log-type-cell"
                            sx={{
                              ...logBodyCellSx,
                              backgroundColor: `${typeVis.bg} !important`,
                              color: contrastOnHex(typeVis.bg),
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {typeVis.label}
                          </TableCell>
                          <TableCell sx={logBodyCellSx}>{name || "—"}</TableCell>
                          <TableCell sx={logBodyCellSx}>{row.chemistName || "—"}</TableCell>
                          <TableCell sx={logBodyCellSx}>{row.staffName || "—"}</TableCell>
                          <TableCell sx={logBodyCellSx}>{dailyLogStatusText(row)}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: `1px solid ${DOCLINK_COLORS.LIGHT_PURPLE}99`,
          px: 2,
          py: 1.5,
        }}
      >
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HubMedDailyLogDialog;
